import axios from "axios";
import { API_URL } from "../constants";

/** A responsible user of a custom (non-system) area — Master-only payload. */
export interface WorkSecurityAreaResponsible {
  id: string;
  name: string;
  email: string;
}

/**
 * An instruction "sphere" (e.g. "Darbų sauga", "Kibernetinis saugumas").
 * See api/models/InstructionAreas.js on the backend for the source of truth.
 */
export interface WorkSecurityArea {
  id: string;
  name: string;
  /** Stable code for the seeded system area (e.g. "work_safety"); null for custom areas. */
  key: string | null;
  /** The area seeded by the migration — cannot be archived, has no responsibles gate. */
  isSystem: boolean;
  /** The area new/legacy data falls back to when nothing else applies. */
  isDefault: boolean;
  /** Permission string that also grants access to this area (system area only). */
  legacyPermission: string | null;
  hasIncidents: boolean;
  sortOrder: number;
  isArchived: boolean;
  /** Only present when the caller is Master (see GET /work-security/areas). */
  responsibles?: WorkSecurityAreaResponsible[];
}

export interface FetchWorkSecurityAreasResult {
  areas: WorkSecurityArea[];
  /** True for Master — gates area create/rename/responsibles management in Settings. */
  canManageAreas: boolean;
}

/**
 * Prop contract the module header (containers/work-security/work-security.tsx)
 * hands to each of the six list tabs, resolved via `useAreaScope`. `areaId`
 * follows the hard rule from that hook: empty string with a single accessible
 * area, otherwise the concrete id or "" for "all areas". Wiring these into a
 * given panel's own fetch call, filters and the "Sritis" column (PR-2 §P3) is
 * a separate follow-up — this interface only makes the header's prop-passing
 * type-safe until then.
 *
 * `areas` (PR-2 §P4) is the full accessible-areas list, threaded through so a
 * panel can hand it to the create-form modal it opens (role/group/test/
 * incident forms need the full list to offer a Select in "all spheres" mode —
 * see features/work-security-areas/AreaFormField.tsx). Optional and additive:
 * panels that only ever used `areaId`/`selectedArea` are unaffected.
 */
export interface WorkSecurityAreaScopeProps {
  areaId?: string;
  selectedArea?: WorkSecurityArea | null;
  areas?: WorkSecurityArea[];
}

/**
 * GET /:companyId/work-security/areas
 * Returns the areas accessible to the caller. `includeArchived` is only
 * honoured by the backend for Master (the area-management screen needs to
 * see and unarchive them); everyone else keeps the unfiltered-by-this-flag view.
 */
export async function fetchWorkSecurityAreas(
  companyId: string,
  opts?: { includeArchived?: boolean }
): Promise<FetchWorkSecurityAreasResult> {
  const qs = opts?.includeArchived ? "?includeArchived=1" : "";
  const res = await axios.get(`${API_URL}/${companyId}/work-security/areas${qs}`);
  return res.data;
}

/** POST /:companyId/work-security/areas — Master only. Always creates a non-system area. */
export async function createWorkSecurityArea(
  companyId: string,
  data: { name: string; hasIncidents: boolean }
): Promise<{ area: WorkSecurityArea }> {
  const res = await axios.post(`${API_URL}/${companyId}/work-security/areas`, data);
  return res.data;
}

/**
 * PATCH /:companyId/work-security/areas/:areaId — Master only. `key`,
 * `legacyPermission`, `isDefault` and `isSystem` are fixed at seed time and
 * are never accepted here; the system area additionally cannot be archived
 * (backend returns `AREA_SYSTEM_ARCHIVE_FORBIDDEN`).
 */
export async function updateWorkSecurityArea(
  companyId: string,
  areaId: string,
  data: { name?: string; hasIncidents?: boolean; isArchived?: boolean; sortOrder?: number }
): Promise<{ area: WorkSecurityArea }> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/work-security/areas/${areaId}`,
    data
  );
  return res.data;
}

/**
 * PUT /:companyId/work-security/areas/:areaId/responsibles — Master only.
 * Full replace: membership here IS the access grant for a non-system area.
 *
 * NOTE: the PR-2 spec doc describes the body as `{ userIds: [] }`, but the
 * shipped backend controller (api/controllers/work-security/areas/set-responsibles.js)
 * reads `req.body.users` — verified against that file, which is the source
 * of truth here. Flagged as a spec/implementation mismatch.
 */
export async function setWorkSecurityAreaResponsibles(
  companyId: string,
  areaId: string,
  userIds: string[]
): Promise<{ responsibles: Array<{ id: string; user: WorkSecurityAreaResponsible }> }> {
  const res = await axios.put(
    `${API_URL}/${companyId}/work-security/areas/${areaId}/responsibles`,
    { users: userIds }
  );
  return res.data;
}
